import React, { useState, useEffect } from 'react';
import { OrdersService, type PedidoResponse } from '../services/orders/orders.service';
import { webSocketService } from '../services/websocket/websocket.service';
import { PedidoDetalle } from '../components/orders/PedidoDetalle';
import { 
  Package, 
  Search, 
  MapPin,
  ChevronRight,
  ArrowLeft,
  XCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';

export const OrderTrackingPage: React.FC = () => {
  const [numeroPedido, setNumeroPedido] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [pedido, setPedido] = useState<PedidoResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const buscarPedido = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!numeroPedido.trim() || !email.trim()) return;

    setLoading(true);
    setError(null);
    setPedido(null);

    try {
      // Primero buscamos por número
      const response: any = await OrdersService.getByNumber(numeroPedido.trim());
      
      if (response.success) {
        // Verificamos que el email coincida
        if (response.data.cliente.email.toLowerCase() === email.trim().toLowerCase()) {
          setPedido(response.data);
        } else {
          setError('El número de pedido no coincide con el email proporcionado');
        }
      }
    } catch (err: any) {
      setError('No se encontró ningún pedido con esos datos');
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="min-h-screen py-4 px-4 bg-gray-50">
      <div className="max-w-[1600px] mx-auto">
        {/* Header - Solo mostrar cuando no hay pedido */}
        {!pedido && (
          <div className="text-center mb-8">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 mb-4 text-sm hover:underline text-gray-600"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver al inicio
            </Link>
            <h1 className="text-3xl font-bold mb-2 text-gray-900">
              Seguimiento de Pedido
            </h1>
            <p className="text-gray-600">
              Consulta el estado de tu pedido en tiempo real
            </p>
          </div>
        )}

        {/* Formulario de búsqueda - Solo mostrar cuando no hay pedido */}
        {!pedido && (
          <div className="rounded-2xl shadow-lg p-6 md:p-8 mb-8 max-w-4xl mx-auto bg-white">
            <form onSubmit={buscarPedido}>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-end">
                {/* Campo número de pedido */}
                <div className="lg:col-span-5">
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    Número de pedido
                  </label>
                  <div className="relative">
                    <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={numeroPedido}
                      onChange={(e) => setNumeroPedido(e.target.value)}
                      placeholder="Ej: PED-20260325-0001"
                      className="w-full pl-10 pr-4 py-3 rounded-xl border-2 transition-all bg-gray-50 border-gray-200 focus:border-emerald-500"
                    />
                  </div>
                </div>
                
                {/* Campo email */}
                <div className="lg:col-span-5">
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    Email de confirmación
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@email.com"
                      className="w-full pl-10 pr-4 py-3 rounded-xl border-2 transition-all bg-gray-50 border-gray-200 focus:border-emerald-500"
                    />
                  </div>
                </div>
                
                {/* Botón */}
                <div className="lg:col-span-2">
                  <button
                    type="submit"
                    disabled={loading || !numeroPedido.trim() || !email.trim()}
                    className="w-full px-6 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                        Buscando...
                      </>
                    ) : (
                      <>
                        <Search className="w-5 h-5" />
                        Buscar
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>

            {/* Error */}
            {error && (
              <div className="mt-4 p-4 rounded-xl flex items-center gap-3 bg-red-50 border border-red-200">
                <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <p className="text-red-700">{error}</p>
              </div>
            )}
          </div>
        )}

        {pedido && (
          <PedidoDetalle
            pedido={pedido}
            onPedidoChange={setPedido}
            headerAction={
              <Link
                to="/pedidos"
                onClick={() => {
                  setPedido(null);
                  setNumeroPedido('');
                  setEmail('');
                }}
                className="inline-flex items-center gap-1 text-xs hover:underline text-gray-600"
              >
                <ArrowLeft className="w-3 h-3" />
                Volver a buscar
              </Link>
            }
          />
        )}

        {/* Ayuda */}
        {!pedido && (
          <div className="rounded-2xl shadow-lg p-8 text-center bg-white">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 bg-gray-100">
              <Package className="w-10 h-10 text-gray-500" />
            </div>
            <h3 className="font-semibold mb-2 text-lg text-gray-900">
              ¿Necesitas ayuda?
            </h3>
            <p className="text-sm mb-4 max-w-md mx-auto text-gray-600">
              Si tienes problemas para localizar tu pedido, contáctanos y te ayudaremos a encontrarlo
            </p>
            <Link
              to="/contacto"
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 font-medium transition-colors"
            >
              Contactar soporte
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderTrackingPage;
